import { useCallback, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { auth } from '../firebase'
import { ProcessEvent, ProcessProgress, ProcessSnapshot, TrackedProcessStatus } from '../types'
import { authFetch } from '../utils/authFetch'

interface UseWebSocketReturn {
  progress: ProcessProgress | null
  isConnected: boolean
  sessionId: string | null
  /** `tracked`: a sessão é do `process-tracker` (empresa e grupo). */
  startSession: (newSessionId: string, tracked?: boolean) => void
  clearProgress: () => void
  /** Histórico de etapas da sessão atual (janela de debug). */
  processEvents: ProcessEvent[]
  /** `lost` = o servidor não conhece mais a sessão (reiniciou ou expirou). */
  processStatus: TrackedProcessStatus | null
  processStartedAt: string | null
  /**
   * Pede o cancelamento ao backend e LIBERA A TELA NA HORA: o que a sessão
   * cancelada ainda emitir (a etapa em curso termina no servidor) é ignorado.
   */
  cancelProcess: () => Promise<void>
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3099'
// Force deploy

/**
 * Consulta de status por HTTP enquanto o processamento roda. O socket é o
 * caminho principal; isto cobre o que ele perde (evento emitido durante uma
 * reconexão, sala perdida, pod trocado).
 */
const STATUS_POLL_MS = 15_000

const isTerminal = (p: ProcessProgress | null) => p?.step === 'complete' || p?.step === 'error'

export function useWebSocket(): UseWebSocketReturn {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [progress, setProgress] = useState<ProcessProgress | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [processEvents, setProcessEvents] = useState<ProcessEvent[]>([])
  const [processStatus, setProcessStatus] = useState<TrackedProcessStatus | null>(null)
  const [processStartedAt, setProcessStartedAt] = useState<string | null>(null)

  // Os handlers do socket são registrados uma vez; leem a sessão por ref.
  const sessionIdRef = useRef<string | null>(null)
  const trackedRef = useRef(false)
  // Sessão cancelada pelo painel: o servidor ainda fecha a etapa em curso e
  // emite o "cancelado"; nada disso pode mexer na tela que já foi liberada.
  const abandonedRef = useRef<string | null>(null)

  const applySnapshot = useCallback((forSession: string, snapshot: ProcessSnapshot | null) => {
    if (forSession !== sessionIdRef.current || !trackedRef.current) return
    if (forSession === abandonedRef.current) return
    if (!snapshot) {
      // Sem a sessão no servidor não há o que esperar: destrava a tela.
      setProcessStatus('lost')
      setProgress(prev =>
        isTerminal(prev)
          ? prev
          : {
              step: 'error',
              message: '⚠️ O servidor não tem mais este processamento (reiniciou ou expirou). Rode de novo.',
              timestamp: new Date().toISOString(),
            }
      )
      return
    }
    setProcessStatus(snapshot.status)
    setProcessStartedAt(snapshot.startedAt)
    setProcessEvents(snapshot.events)
    if (snapshot.lastProgress) {
      const last = snapshot.lastProgress as ProcessProgress
      // Só troca se for mais novo: evita refazer o "complete" (que recarrega
      // pedidos no App) a cada snapshot.
      setProgress(prev => (prev && prev.timestamp >= last.timestamp ? prev : last))
    }
  }, [])

  useEffect(() => {
    const connect = async () => {
      const user = auth.currentUser
      if (!user) return

      // Conectar diretamente ao backend (evita proxy issues com WebSocket).
      // `auth` como função: cada RECONEXÃO pega um token novo. Com o token fixo
      // do primeiro connect, a reconexão depois de 1 h era recusada.
      const newSocket = io(API_URL, {
        transports: ['websocket', 'polling'],
        auth: cb => {
          user.getIdToken().then(token => cb({ token }), () => cb({}))
        },
      })

      newSocket.on('connect', () => {
        console.log('✅ WebSocket conectado:', newSocket.id)
        setIsConnected(true)
        // Reconexão volta FORA da sala; sem isto os eventos seguintes (e o
        // `complete`) iam para ninguém e a tela travava na última mensagem.
        if (sessionIdRef.current) newSocket.emit('join-session', sessionIdRef.current)
      })

      newSocket.on('disconnect', () => {
        console.log('❌ WebSocket desconectado')
        setIsConnected(false)
      })

      newSocket.on('progress', (data: ProcessProgress) => {
        console.log('📡 Progresso recebido:', data)
        // Evento de outra sessão (a cancelada, ou uma anterior que ainda está
        // na sala): sem o filtro, o "complete" atrasado dela sobrescreveria a
        // tela do processamento novo.
        if (data.sessionId && data.sessionId !== sessionIdRef.current) return
        if (data.sessionId && data.sessionId === abandonedRef.current) return
        setProgress(data)
      })

      newSocket.on('process-event', (payload: { sessionId: string; event: ProcessEvent }) => {
        if (payload.sessionId !== sessionIdRef.current) return
        setProcessEvents(prev =>
          prev.length > 0 && prev[prev.length - 1].seq >= payload.event.seq ? prev : [...prev, payload.event]
        )
        if (payload.event.kind === 'log' && payload.event.label === 'Cancelado') setProcessStatus('cancelled')
      })

      newSocket.on('session-snapshot', (payload: { sessionId: string; snapshot: ProcessSnapshot | null }) => {
        applySnapshot(payload.sessionId, payload.snapshot)
      })

      setSocket(newSocket)
    }

    connect()

    return () => {
      socket?.close()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Status terminal pelo progresso: o socket pode ter entregue o `complete`
  // sem um snapshot depois.
  useEffect(() => {
    if (progress?.step === 'complete') setProcessStatus(s => (s === 'running' ? 'completed' : s))
    else if (progress?.step === 'error') setProcessStatus(s => (s === 'running' ? 'failed' : s))
  }, [progress])

  // Rede de segurança por HTTP enquanto roda.
  useEffect(() => {
    if (!sessionId || processStatus !== 'running') return
    const id = sessionId
    const timer = setInterval(async () => {
      try {
        const res = await authFetch(`/api/process-full/${encodeURIComponent(id)}/status`)
        if (res.status === 404) return applySnapshot(id, null)
        if (res.ok) applySnapshot(id, (await res.json()) as ProcessSnapshot)
      } catch {
        // Rede fora: tenta no próximo ciclo.
      }
    }, STATUS_POLL_MS)
    return () => clearInterval(timer)
  }, [sessionId, processStatus, applySnapshot])

  const startSession = useCallback(
    (newSessionId: string, tracked = false) => {
      sessionIdRef.current = newSessionId
      trackedRef.current = tracked
      abandonedRef.current = null
      setSessionId(newSessionId)
      setProcessEvents([])
      setProcessStatus(tracked ? 'running' : null)
      setProcessStartedAt(tracked ? new Date().toISOString() : null)
      if (socket) {
        socket.emit('join-session', newSessionId)
        console.log('📍 Sessão iniciada:', newSessionId)
      }
    },
    [socket]
  )

  const cancelProcess = useCallback(async () => {
    const id = sessionIdRef.current
    if (!id || !trackedRef.current) return
    try {
      const res = await authFetch(`/api/process-full/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
      if (!res.ok && res.status !== 409) throw new Error(`HTTP ${res.status}`)
    } catch (err) {
      // Mesmo sem confirmar no servidor, a tela é liberada: quem cancelou quer
      // rodar outra coisa, e o que esta sessão ainda emitir é ignorado.
      console.error('Erro ao cancelar processamento:', err)
    }
    abandonedRef.current = id
    setProcessStatus('cancelled')
    setProgress({
      sessionId: id,
      step: 'error',
      message: '⛔ Processamento cancelado. Ajuste os filtros e processe de novo.',
      timestamp: new Date().toISOString(),
      data: { cancelled: true } as ProcessProgress['data'],
    })
  }, [])

  const clearProgress = useCallback(() => {
    setProgress(null)
  }, [])

  return {
    progress,
    isConnected,
    sessionId,
    startSession,
    clearProgress,
    processEvents,
    processStatus,
    processStartedAt,
    cancelProcess,
  }
}
